import {
    AUDIO_LEVEL_THRESHOLDS,
    AUDIO_UPLOAD_CHUNK_CUT_WINDOW_START_MS,
    AUDIO_UPLOAD_CHUNK_TARGET_MS,
    RECORDER_STATE,
} from "./constants";

const STATIC_RECORDER_STATUS_META = {
    [RECORDER_STATE.IDLE]: {
        text: "",
        color: "oslo",
    },
    [RECORDER_STATE.INITIALIZING]: {
        text: "กำลังเริ่มอัด...",
        color: "oslo",
    },
    [RECORDER_STATE.PAUSED]: {
        text: "หยุดชั่วคราว",
        color: "pizza",
    },
    [RECORDER_STATE.SUMMARIZING]: {
        text: "กำลังสรุป..",
        color: "cerulean_blue",
    },
    [RECORDER_STATE.COMPLETED]: {
        text: "เสร็จสิ้น",
        color: "fun_green",
    },
    [RECORDER_STATE.UNSUPPORTED]: {
        text: "อุปกรณ์ไม่รองรับ",
        color: "coral",
    },
    [RECORDER_STATE.ERROR]: {
        text: "ระบบผิดพลาด",
        color: "coral",
    },
};

const AUDIO_INTENSITY_STATUS_META = {
    silent: {
        text: "ไม่ได้ยินเสียง",
        color: "pizza",
    },
    low: {
        text: "เสียงเบา",
        color: "pizza",
    },
    normal: {
        text: "กำลังฟัง..",
        color: "oslo",
    },
};

export function clampAudioLevel(value) {
    return Math.max(0, Math.min(1, Number(value) || 0));
}

export function formatElapsedTime(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function getAudioIntensity(audioLevel) {
    if (audioLevel <= AUDIO_LEVEL_THRESHOLDS.silentMax) return "silent";
    if (audioLevel <= AUDIO_LEVEL_THRESHOLDS.lowMax) return "low";
    return "normal";
}

export function getRecorderStatusMeta(recorderState, errorMessage, audioLevel) {
    if (errorMessage) {
        return STATIC_RECORDER_STATUS_META[RECORDER_STATE.ERROR];
    }

    if (recorderState === RECORDER_STATE.RECORDING) {
        return AUDIO_INTENSITY_STATUS_META[getAudioIntensity(audioLevel)];
    }

    return STATIC_RECORDER_STATUS_META[recorderState] || STATIC_RECORDER_STATUS_META[RECORDER_STATE.ERROR];
}

export function getPreferredMimeType() {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
        return "";
    }

    const candidates = [
        "audio/ogg;codecs=opus",
        "audio/ogg",
        "audio/mp4;codecs=mp4a.40.2",
        "audio/mp4",
        "audio/aac",
        "audio/mpeg",
    ];

    return candidates.find((type) => window.MediaRecorder.isTypeSupported(type)) || "";
}

export function getBufferedDurationMs(bufferedChunks) {
    return bufferedChunks.reduce((total, chunk) => total + (chunk.durationMs || 0), 0);
}

function getChunkLevelSamples(chunk) {
    if (Array.isArray(chunk?.levelSamples) && chunk.levelSamples.length) {
        return chunk.levelSamples;
    }

    if (chunk?.durationMs > 0) {
        return [{
            level: chunk.averageLevel ?? 1,
            offsetMs: chunk.durationMs,
        }];
    }

    return [];
}

function collectBufferedAudioLevelSamples(bufferedChunks) {
    const collectedSamples = [];
    let cumulativeDurationMs = 0;

    bufferedChunks.forEach((chunk) => {
        const chunkDurationMs = chunk.durationMs || 0;

        // แปลง sample ของแต่ละก้อนให้เป็น timeline เดียวกันทั้ง buffer
        getChunkLevelSamples(chunk).forEach((sample) => {
            collectedSamples.push({
                level: sample.level ?? chunk.averageLevel ?? 1,
                offsetMs: cumulativeDurationMs + Math.max(
                    0,
                    Math.min(chunkDurationMs, sample.offsetMs ?? chunkDurationMs)
                ),
            });
        });

        cumulativeDurationMs += chunkDurationMs;
    });

    return collectedSamples;
}

export function findQuietAudioCutDurationMs(bufferedChunks) {
    if (!bufferedChunks.length) {
        return -1;
    }

    const bufferedDurationMs = getBufferedDurationMs(bufferedChunks);
    const collectedSamples = collectBufferedAudioLevelSamples(bufferedChunks);
    let quietestCandidate = null;
    let latestCandidateWithinWindow = -1;

    collectedSamples.forEach((sample) => {
        const isInsideCutWindow = (
            sample.offsetMs >= AUDIO_UPLOAD_CHUNK_CUT_WINDOW_START_MS
            && sample.offsetMs <= AUDIO_UPLOAD_CHUNK_TARGET_MS
        );

        if (!isInsideCutWindow) {
            return;
        }

        latestCandidateWithinWindow = Math.max(
            latestCandidateWithinWindow,
            sample.offsetMs
        );

        // ถ้าระดับเสียงเท่ากัน ให้เลือกจุดที่อยู่ท้ายกว่าเพื่อให้ chunk ยาวใกล้ 25 วิที่สุด
        if (
            !quietestCandidate
            || sample.level < quietestCandidate.level
            || (
                sample.level === quietestCandidate.level
                && sample.offsetMs > quietestCandidate.offsetMs
            )
        ) {
            quietestCandidate = sample;
        }
    });

    if (quietestCandidate) {
        return quietestCandidate.offsetMs;
    }

    if (latestCandidateWithinWindow >= 0) {
        return latestCandidateWithinWindow;
    }

    return Math.min(bufferedDurationMs, AUDIO_UPLOAD_CHUNK_TARGET_MS);
}

export function getMimeTypeExtension(mimeType) {
    const normalizedMimeType = String(mimeType || "").toLowerCase();

    if (normalizedMimeType.includes("flac")) return "flac";
    if (normalizedMimeType.includes("aac")) return "aac";
    if (normalizedMimeType.includes("ogg")) return "ogg";
    if (normalizedMimeType.includes("opus")) return "opus";
    if (normalizedMimeType.includes("mp4") || normalizedMimeType.includes("m4a")) return "m4a";
    if (normalizedMimeType.includes("wma")) return "wma";
    if (normalizedMimeType.includes("mpeg")) return "mp3";
    if (normalizedMimeType.includes("wav") || normalizedMimeType.includes("wave")) return "wav";

    return "bin";
}

export function buildAudioChunkFile(blob, { cuid, order, mimeType }) {
    const extension = getMimeTypeExtension(mimeType || blob?.type);
    const filename = `${cuid || "presscribe-session"}-chunk-${String(order).padStart(3, "0")}.${extension}`;

    return new File([blob], filename, {
        type: mimeType || blob?.type || "application/octet-stream",
        lastModified: Date.now(),
    });
}

function getAudioContextCtor() {
    if (typeof window === "undefined") {
        return null;
    }

    return window.AudioContext || window.webkitAudioContext || null;
}

function writeAsciiString(view, offset, value) {
    for (let index = 0; index < value.length; index += 1) {
        view.setUint8(offset + index, value.charCodeAt(index));
    }
}

function clampPcmSample(value) {
    return Math.max(-1, Math.min(1, value));
}

function encodeAudioBufferToWav(audioBuffer) {
    const channelCount = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const frameCount = audioBuffer.length;
    const bytesPerSample = 2;
    const blockAlign = channelCount * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataByteLength = frameCount * blockAlign;
    const wavBuffer = new ArrayBuffer(44 + dataByteLength);
    const view = new DataView(wavBuffer);

    writeAsciiString(view, 0, "RIFF");
    view.setUint32(4, 36 + dataByteLength, true);
    writeAsciiString(view, 8, "WAVE");
    writeAsciiString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, channelCount, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeAsciiString(view, 36, "data");
    view.setUint32(40, dataByteLength, true);

    const channelData = Array.from(
        { length: channelCount },
        (_, channelIndex) => audioBuffer.getChannelData(channelIndex)
    );

    let offset = 44;
    for (let frameIndex = 0; frameIndex < frameCount; frameIndex += 1) {
        for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
            const sample = clampPcmSample(channelData[channelIndex][frameIndex] || 0);
            const pcmValue = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
            view.setInt16(offset, pcmValue, true);
            offset += bytesPerSample;
        }
    }

    return wavBuffer;
}

function concatenateDecodedAudioBuffers(audioBuffers) {
    if (!audioBuffers.length) {
        throw new Error("No audio buffers to concatenate");
    }

    const channelCount = Math.max(
        ...audioBuffers.map((audioBuffer) => audioBuffer.numberOfChannels || 1)
    );
    const totalFrameCount = audioBuffers.reduce(
        (totalFrames, audioBuffer) => totalFrames + audioBuffer.length,
        0
    );
    const sampleRate = audioBuffers[0].sampleRate;
    const OfflineAudioContextCtor = typeof window === "undefined"
        ? null
        : window.OfflineAudioContext || window.webkitOfflineAudioContext;

    if (!OfflineAudioContextCtor) {
        throw new Error("OfflineAudioContext is not supported");
    }

    const outputBuffer = new OfflineAudioContextCtor(
        channelCount,
        totalFrameCount,
        sampleRate
    ).createBuffer(channelCount, totalFrameCount, sampleRate);

    let writeOffset = 0;
    audioBuffers.forEach((audioBuffer) => {
        for (let channelIndex = 0; channelIndex < channelCount; channelIndex += 1) {
            const targetChannel = outputBuffer.getChannelData(channelIndex);
            if (channelIndex < audioBuffer.numberOfChannels) {
                targetChannel.set(audioBuffer.getChannelData(channelIndex), writeOffset);
            }
        }
        writeOffset += audioBuffer.length;
    });

    return outputBuffer;
}

function sliceAudioBuffer(audioBuffer, startFrame, endFrame) {
    const clampedStartFrame = Math.max(0, Math.min(audioBuffer.length, startFrame));
    const clampedEndFrame = Math.max(clampedStartFrame, Math.min(audioBuffer.length, endFrame));
    const frameCount = clampedEndFrame - clampedStartFrame;
    if (!frameCount) {
        return null;
    }

    const OfflineAudioContextCtor = typeof window === "undefined"
        ? null
        : window.OfflineAudioContext || window.webkitOfflineAudioContext;

    if (!OfflineAudioContextCtor) {
        throw new Error("OfflineAudioContext is not supported");
    }

    const slicedBuffer = new OfflineAudioContextCtor(
        audioBuffer.numberOfChannels,
        frameCount,
        audioBuffer.sampleRate
    ).createBuffer(
        audioBuffer.numberOfChannels,
        frameCount,
        audioBuffer.sampleRate
    );

    for (let channelIndex = 0; channelIndex < audioBuffer.numberOfChannels; channelIndex += 1) {
        const channelData = audioBuffer
            .getChannelData(channelIndex)
            .slice(clampedStartFrame, clampedEndFrame);
        slicedBuffer.getChannelData(channelIndex).set(channelData);
    }

    return slicedBuffer;
}

function audioBufferToWavBlob(audioBuffer) {
    if (!audioBuffer || !audioBuffer.length) {
        return new Blob([], { type: "audio/wav" });
    }

    return new Blob([encodeAudioBufferToWav(audioBuffer)], { type: "audio/wav" });
}

async function decodeAudioChunksSafely(audioContext, audioChunks) {
    const decodedAudioBuffers = [];

    for (const audioChunk of audioChunks) {
        if (
            !audioChunk?.blob
            || audioChunk.blob.size === 0
            || typeof audioChunk.blob.arrayBuffer !== "function"
        ) {
            continue;
        }

        const arrayBuffer = await audioChunk.blob.arrayBuffer();
        if (!arrayBuffer.byteLength) {
            continue;
        }

        try {
            const decodedAudio = await audioContext.decodeAudioData(arrayBuffer.slice(0));
            decodedAudioBuffers.push(decodedAudio);
        } catch {
            // ข้ามก้อนที่ decode ไม่ได้ เพื่อไม่ให้ stop/pause/finalize ล้มทั้ง flow
            continue;
        }
    }

    return decodedAudioBuffers;
}

export async function splitBufferedAudioAtDuration(bufferedChunks, splitDurationMs) {
    const totalDurationMs = getBufferedDurationMs(bufferedChunks);
    const normalizedSplitDurationMs = Math.max(
        0,
        Math.min(totalDurationMs, splitDurationMs)
    );

    if (!bufferedChunks.length || normalizedSplitDurationMs <= 0) {
        return {
            remainingChunks: bufferedChunks,
            uploadBlob: new Blob([], { type: "audio/wav" }),
            uploadDurationMs: 0,
        };
    }

    const AudioContextCtor = getAudioContextCtor();
    if (!AudioContextCtor) {
        throw new Error("AudioContext is not supported");
    }

    const audioContext = new AudioContextCtor();

    try {
        const decodedAudioBuffers = await decodeAudioChunksSafely(
            audioContext,
            bufferedChunks
        );

        if (!decodedAudioBuffers.length) {
            return {
                remainingChunks: bufferedChunks,
                uploadBlob: new Blob([], { type: "audio/wav" }),
                uploadDurationMs: 0,
            };
        }

        // รวมทุก segment เป็น PCM ก้อนเดียวก่อนตัด เพื่อไม่ให้เหลือแค่เสียงก้อนแรกของ container เดิม
        const combinedAudioBuffer = concatenateDecodedAudioBuffers(decodedAudioBuffers);
        const splitFrame = Math.max(
            0,
            Math.min(
                combinedAudioBuffer.length,
                Math.round((normalizedSplitDurationMs / 1000) * combinedAudioBuffer.sampleRate)
            )
        );
        const uploadAudioBuffer = sliceAudioBuffer(combinedAudioBuffer, 0, splitFrame);
        const remainingAudioBuffer = sliceAudioBuffer(
            combinedAudioBuffer,
            splitFrame,
            combinedAudioBuffer.length
        );
        const allLevelSamples = collectBufferedAudioLevelSamples(bufferedChunks);
        const remainingLevelSamples = allLevelSamples
            .filter((sample) => sample.offsetMs >= normalizedSplitDurationMs)
            .map((sample) => ({
                level: sample.level,
                offsetMs: sample.offsetMs - normalizedSplitDurationMs,
            }));
        const remainingDurationMs = Math.max(0, totalDurationMs - normalizedSplitDurationMs);
        const remainingAverageLevel = remainingLevelSamples.length
            ? remainingLevelSamples.reduce((total, sample) => total + sample.level, 0) / remainingLevelSamples.length
            : 0;

        return {
            remainingChunks: remainingDurationMs > 0
                ? [{
                    averageLevel: remainingAverageLevel,
                    blob: audioBufferToWavBlob(remainingAudioBuffer),
                    durationMs: remainingDurationMs,
                    levelSamples: remainingLevelSamples,
                }]
                : [],
            uploadBlob: audioBufferToWavBlob(uploadAudioBuffer),
            uploadDurationMs: normalizedSplitDurationMs,
        };
    } finally {
        if (audioContext.state !== "closed") {
            await audioContext.close().catch(() => {});
        }
    }
}

export async function buildCombinedAudioChunkBlob(audioChunks) {
    if (!audioChunks.length) {
        return new Blob([], { type: "audio/wav" });
    }

    const AudioContextCtor = getAudioContextCtor();
    if (!AudioContextCtor) {
        throw new Error("AudioContext is not supported");
    }

    const audioContext = new AudioContextCtor();

    try {
        const decodedAudioBuffers = await decodeAudioChunksSafely(
            audioContext,
            audioChunks
        );

        if (!decodedAudioBuffers.length) {
            return new Blob([], { type: "audio/wav" });
        }

        // ใช้ตอน pause/stop เพื่ออัปโหลด remainder ทั้งก้อนแบบไฟล์เดียว
        const combinedAudioBuffer = concatenateDecodedAudioBuffers(decodedAudioBuffers);
        const wavBuffer = encodeAudioBufferToWav(combinedAudioBuffer);

        return new Blob([wavBuffer], { type: "audio/wav" });
    } finally {
        if (audioContext.state !== "closed") {
            await audioContext.close().catch(() => {});
        }
    }
}
